/**
 * The captcha control (U6) — Cloudflare Turnstile, mounted above Submit when
 * the public form says it asks for one.
 *
 * The script is loaded once per page, on demand: a form without a captcha
 * never touches Cloudflare. The widget hands its token up through `onToken`
 * (an empty string when it expires or errors), and `resetKey` changing tells
 * the widget to start over — what the page does after the server refused the
 * token. Errors reach the person exactly as a field's do: the same
 * `aria-invalid`, `data-error-path` and message line, so the summary's item
 * focuses this box.
 */
import { useEffect, useRef, useState } from 'react';
import { matchesNow } from '../shared/media';
import { STRINGS as S } from './strings';

/**
 * The widget's normal size is 300px wide, which overflows a phone's column
 * (M2/C2). Cloudflare's 'compact' size is 130px wide and takes the same
 * token, so nothing about the flow or the server's check changes.
 */
const NARROW_CAPTCHA = '(max-width: 399px)';

export const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
/** The errors-map key the page and the summary use for this control. */
export const CAPTCHA_PATH = '_captcha';

interface TurnstileApi {
  render(el: HTMLElement, options: Record<string, unknown>): string;
  reset(id?: string): void;
  remove(id: string): void;
}

declare global {
  interface Window { turnstile?: TurnstileApi }
}

let loading: Promise<TurnstileApi> | null = null;

/** Load Cloudflare's script once; resolve with its API. */
export function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (loading) return loading;
  loading = new Promise<TurnstileApi>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = TURNSTILE_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error('turnstile did not load'));
    };
    script.onerror = () => { loading = null; reject(new Error('turnstile script failed')); };
    document.head.appendChild(script);
  });
  return loading;
}

export function CaptchaField({ siteKey, onToken, resetKey = 0, error }: {
  siteKey: string;
  onToken: (token: string) => void;
  /** bump to make the widget start over (after a refused token) */
  resetKey?: number;
  /** the current error for this control, from the pager's map */
  error?: string;
}) {
  const box = useRef<HTMLDivElement>(null);
  const widget = useRef<string | null>(null);
  const latest = useRef(onToken);
  latest.current = onToken;
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadTurnstile()
      .then((api) => {
        if (cancelled || !box.current) return;
        widget.current = api.render(box.current, {
          sitekey: siteKey,
          theme: 'auto',
          ...(matchesNow(NARROW_CAPTCHA) ? { size: 'compact' } : {}),
          callback: (token: string) => latest.current(token),
          'expired-callback': () => latest.current(''),
          'error-callback': () => latest.current(''),
        });
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => {
      cancelled = true;
      if (widget.current && window.turnstile) {
        try { window.turnstile.remove(widget.current); } catch { /* already gone */ }
      }
      widget.current = null;
    };
  }, [siteKey]);

  useEffect(() => {
    if (!resetKey || !widget.current || !window.turnstile) return;
    try { window.turnstile.reset(widget.current); } catch { /* widget gone */ }
    latest.current('');
  }, [resetKey]);

  const errId = 'pf-captcha-err';
  return (
    <div
      className={`of-field of-captcha${error ? ' has-error' : ''}`}
      data-error-path={CAPTCHA_PATH}
      tabIndex={-1}
      role="group"
      aria-label={S.captcha.label}
      aria-invalid={error ? 'true' : undefined}
      aria-describedby={error ? errId : undefined}
    >
      <div ref={box} />
      {failed && <div className="of-field-error">{S.captcha.unavailable}</div>}
      {error && <div className="of-error of-field-error" id={errId} role="alert">{error}</div>}
    </div>
  );
}
